import DOMPurify from "dompurify";

// Shared tight allowlist for server-emitted / external rich-text HTML —
// paragraphs, line breaks, links, basic emphasis. Excludes scripts, styles,
// event handlers, and everything else. Used by artifact summary excerpts
// (ArtifactBiscuit) and calendar event descriptions.
const ALLOWED_TAGS = ["p", "br", "a", "strong", "b", "em", "i", "u", "ul", "ol", "li"];
const ALLOWED_ATTR = ["href"];

export type SanitizeRichTextOptions = {
  // Force surviving links to open in a new tab with safe rel attributes.
  openLinksInNewTab?: boolean;
};

// Its own instance, so this hook never runs for any other sanitize call in the app.
const newTabPurifier = DOMPurify(window);
newTabPurifier.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

/**
 * Sanitize untrusted rich-text HTML against a tight allowlist: strips scripts,
 * styles, event handlers, and any tag or attribute outside the allowlist.
 */
export function sanitizeRichTextHtml(
  html: string,
  { openLinksInNewTab = false }: SanitizeRichTextOptions = {},
): string {
  const purifier = openLinksInNewTab ? newTabPurifier : DOMPurify;
  return purifier.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR });
}
